import React, { useContext, useEffect, useMemo, useState } from "react";
import { ClientContext, PermissionContext, UserContext } from "./Context";
import { hasPermission, hasAnyPermission } from "./helpers/permissions";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";

export const PermissionProvider = ({ children }) => {
  const location = useLocation();
  const isClientSign = location.pathname.startsWith("/client-sign");

  // Hooks SIEMPRE se ejecutan
  const userCtx = useContext(UserContext);
  const clientCtx = useContext(ClientContext);

  // Elegimos el contexto
  const activeContext = isClientSign ? clientCtx : userCtx;

  const userData = activeContext?.userData ?? null;
  const user = userData?.user ?? null;
  const role = user?.rol_relation ?? null;
  const [newRoleConfig, setNewRoleConfig] = useState(null);

  // Restaurar modo prueba desde sessionStorage al cargar
  useEffect(() => {
    const storedRole = sessionStorage.getItem("testRole");
    if (storedRole) {
      setNewRoleConfig(JSON.parse(storedRole));
      Swal.fire({
        icon: "info",
        title: "MODO PRUEBAS ACTIVO",
        text: `Estás usando temporalmente el rol "${JSON.parse(storedRole).type}"`,
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }, []);

  // Persistir o eliminar en sessionStorage cuando cambia el modo prueba
  useEffect(() => {
    if (newRoleConfig) {
      sessionStorage.setItem("testRole", JSON.stringify(newRoleConfig));
    } else {
      sessionStorage.removeItem("testRole");
    }
  }, [newRoleConfig]);

  // Aviso nativo al recargar o cerrar la pestaña solo si está en modo prueba
  useEffect(() => {
    if (!newRoleConfig) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "Saldrás del modo de pruebas y se restaurará tu rol original.";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [newRoleConfig]);

  const value = useMemo(
    () => ({
      can: (module, action) =>
        hasPermission(newRoleConfig ?? role, module, action),
      canAny: (module, actions) =>
        hasAnyPermission(newRoleConfig ?? role, module, actions),

      newRoleConfig,
      setNewRoleConfig,

      // Función para salir del modo prueba desde la app
      exitTestMode: async () => {
        if (!newRoleConfig) return;

        const confirm = await Swal.fire({
          title: "Salir del modo de pruebas",
          text: `Se restaurará tu rol original: "${role?.type}"`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Restaurar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#1ab394",
        });

        if (confirm.isConfirmed) {
          setNewRoleConfig(null);
          Swal.fire({
            icon: "success",
            title: "Modo de prueba finalizado",
            text: `Ahora estás usando tu rol original: "${role?.type}"`,
            timer: 1800,
            showConfirmButton: false,
          });
        }
      },
    }),
    [newRoleConfig, role],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions debe usarse dentro de PermissionProvider");
  }
  return context;
};
